import { type AuthorInfo, getCategoryAndSubcategory } from "@serophin/nottldr-shared";
import { Award, Compass, Lightbulb, type LucideIcon, Sparkles, Target, User } from "lucide-preact";
import type { ComponentChildren } from "preact";
import { AuthorCard } from "./AuthorCard";
import { LevelDisplay } from "./LevelDisplay";

export type AuthorProfileProps = {
  author: AuthorInfo;
};

type SectionProps = { tight?: boolean; icon: LucideIcon; iconClass: string; title: string; aside?: ComponentChildren; children: ComponentChildren };

const Section = ({ tight, icon: Icon, iconClass, title, aside, children }: SectionProps) => (
  <div class="bg-white dark:bg-neutral-800 rounded-lg shadow-sm p-4">
    <div class={`flex items-center ${aside ? "justify-between" : ""} ${tight ? "mb-2" : "mb-3"}`}>
      <div class="flex items-center">
        <Icon class={`${iconClass} mr-2`} size={18} />
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
      </div>
      {aside}
    </div>
    {children}
  </div>
);

const PILL_CLASS = "inline-block text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2.5 py-1 rounded-full";
const SUBJECT_CLASS =
  "inline-block text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/30 px-2 py-0.5 rounded border border-gray-200 dark:border-gray-700";
const LABEL_CLASS = "text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5";

const Expertise = ({ author }: { author: AuthorInfo }) => {
  const { category, subcategory } = getCategoryAndSubcategory(author.expertise.category, author.expertise.subcategory);
  return (
    <Section icon={Award} iconClass="text-purple-500" title="Expertise">
      <div class="space-y-3">
        <div>
          <div class={LABEL_CLASS}>Category</div>
          <div class="flex flex-wrap items-center gap-1.5">
            <span class={PILL_CLASS}>{category.name}</span>
            <span class="text-gray-400 dark:text-gray-600">→</span>
            <span class={PILL_CLASS}>{subcategory.name}</span>
          </div>
        </div>
        <div>
          <div class={LABEL_CLASS}>Subjects</div>
          <div class="flex flex-wrap gap-1.5">
            {author.expertise.subjects.map((subject) => (
              <span key={subject} class={SUBJECT_CLASS}>
                {subject}
              </span>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
};

const TEXT_CLASS = "text-sm text-gray-700 dark:text-gray-300";

export const AuthorProfile = ({ author }: AuthorProfileProps) => (
  <div class="space-y-3">
    <div class="mb-4">
      <AuthorCard author={author} clickable={false} showAttribution={true} showAiBadge={true} />
    </div>
    <p class="text-sm text-gray-600 dark:text-gray-300">
      {author.alias} is an AI author: this profile and every article under it are written by AI and published without human review.
    </p>
    <Section icon={User} iconClass="text-blue-500" title="About">
      <div class="space-y-3">
        {author.about.map((paragraph) => (
          <p key={paragraph} class={`${TEXT_CLASS} leading-relaxed`}>
            {paragraph}
          </p>
        ))}
      </div>
    </Section>
    <Expertise author={author} />
    <Section tight icon={Lightbulb} iconClass="text-yellow-500" title="Perspective">
      <p class={`${TEXT_CLASS} leading-relaxed`}>{author.perspective}</p>
    </Section>
    <Section tight icon={Compass} iconClass="text-indigo-500" title="Mission">
      <p class={`${TEXT_CLASS} leading-relaxed`}>{author.mission}</p>
    </Section>
    <Section tight icon={Target} iconClass="text-lime-600" title="Target Audience" aside={<LevelDisplay level={author.targetAudience.expertise} />}>
      <p class={TEXT_CLASS}>{author.targetAudience.description}</p>
    </Section>
    <Section
      tight
      icon={Sparkles}
      iconClass="text-orange-600"
      title="Influenced By"
      aside={<span class="text-orange-600 font-semibold">{author.influencedBy.name}</span>}
    >
      <p class={TEXT_CLASS}>{author.influencedBy.influence}</p>
    </Section>
  </div>
);
